import { Alert, DateRange, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import useFirebase from 'components/firebase/useFirebase'
import React, { useEffect, useState } from 'react'

import { Board } from './components/filter/Board'
import { PivotTableRender } from './components/table/PivotTableRender'
import { TreeBuilder } from './TreeBuilder'
import { Aggregator, Dictionary, IgnoredTreeKeys, TreeRoot } from './types'

export type PivotTableProps<T extends any> = {
  data: T[]
  title: string
  keyMapping: Map<
    keyof T,
    { keyName: string; formatter?: (value: string) => string; ordenator?: (a: string, b: string) => number }
  >
  dateRangeFilter: DateRange
}
interface PivotTableContextType {
  total: number
  suffix?: string
}

export const PivotTableContext = React.createContext<PivotTableContextType>(undefined)

export function PivotTable<T>(props: PivotTableProps<T>) {
  const { data, keyMapping, title, dateRangeFilter } = props

  const [dataKeyValues, setDataKeyValues] = useState<Map<keyof T, Array<string>>>()

  const [filterDataKeyValues, setFilterDataKeyValue] = useState<Map<keyof T, Set<string>>>()

  const [rowKeys, setRowKeys] = useState<Array<keyof T>>([])

  const [columnKeys, setColumnKeys] = useState<Array<keyof T>>([])

  const [defaultTree, setDefaultTree] = useState<Dictionary<T> & TreeRoot>()

  const [complemetaryTree, setComplementaryTree] = useState<Dictionary<T> & TreeRoot>()

  const [aggregator, setAggregator] = useState<Aggregator>()

  const [aggregatorKey, setAggregatorKey] = useState<keyof T>()

  const { analytics } = useFirebase()

  const alert = useAlert()

  const handleAggregatorKey = (key: keyof T) => setAggregatorKey(key)
  const handleAggregator = (aggregator: Aggregator) => {
    setAggregator(() => aggregator)
  }

  useEffect(() => {
    const tempKeysValues = new Map<keyof T, Set<string>>()

    data.forEach((element: any) => {
      Object.keys(element)
        .filter((k) => !IgnoredTreeKeys.includes(k))
        .forEach((key) => {
          const keyValue = element[key]
          if (keyValue || keyValue === 0) {
            let set = tempKeysValues.get(key as keyof T)
            if (!set) {
              set = new Set<string>()
              tempKeysValues.set(key as keyof T, set)
            }
            set.add(keyValue)
          }
        })
    })
    const uniqueKeysValues = new Map<keyof T, Array<string>>()
    tempKeysValues.forEach((value, key) => {
      uniqueKeysValues.set(key, Array.from(value).sort(keyMapping.get(key)?.ordenator))
    })
    setDataKeyValues(uniqueKeysValues)
  }, [data, keyMapping])

  useEffect(() => {
    if (rowKeys.length > 0 && columnKeys.length > 0) {
      setComplementaryTree(
        TreeBuilder.group(
          data,
          [...columnKeys, ...rowKeys],
          data.length,
          filterDataKeyValues,
          aggregator,
          aggregatorKey
        )
      )
    }
    const defaultTree = TreeBuilder.group(
      data,
      [...rowKeys, ...columnKeys],
      data.length,
      filterDataKeyValues,
      aggregator,
      aggregatorKey
    )
    setDefaultTree(defaultTree)

    if (defaultTree?.nodeValue === undefined) {
      alert('warning', 'Os filtros selecionados ou a combinação de linhas e colunas não possuem dados')
    }
  }, [data, rowKeys, columnKeys, filterDataKeyValues, aggregator, aggregatorKey, alert])

  const handleSubmit = (values: [Array<keyof T>, Array<keyof T>], newFilters: Map<keyof T, Set<string>>) => {
    const [newRowKeys, newColumnKeys] = values

    if (newRowKeys.length === 0 && newColumnKeys.length === 0) {
      alert('warning', 'Arraste pelo menos um dos campos disponíveis para os quadros de linhas ou colunas.')
      return
    }

    for (let rowKey of newRowKeys) {
      const size = newFilters?.get(rowKey)?.size
      if (!size) {
        alert('warning', 'Nenhum filtro foi selecionado para os campos presentes nos quadros de linhas e colunas.')
        return
      }
    }

    for (let columnKey of newColumnKeys) {
      const size = newFilters?.get(columnKey)?.size

      if (!size) {
        alert('warning', 'Nenhum filtro foi selecionado para os campos presentes nos quadros de linhas e colunas.')
        return
      }
    }

    analytics.logEvent('relatorios_bi_gerar_tabela', {
      'Relatório gerado: ': title,
      'Linhas: ': newRowKeys.map((r) => keyMapping.get(r).keyName),
      'Colunas: ': newColumnKeys.map((c) => keyMapping.get(c).keyName),
    })

    setFilterDataKeyValue(newFilters)
    setRowKeys(newRowKeys)
    setColumnKeys(newColumnKeys)

    if (newRowKeys !== rowKeys || newColumnKeys !== columnKeys || newFilters !== filterDataKeyValues) {
      setComplementaryTree(undefined)
      setDefaultTree(undefined)
    }
  }

  const handleReset = () => {
    setComplementaryTree(undefined)
    setDefaultTree(undefined)
    setRowKeys([])
    setColumnKeys([])
  }

  if (dataKeyValues) {
    return (
      <VFlow>
        <Alert inline type='info'>
          Para gerar uma tabela, arraste os <b>Campos Disponíveis</b> desejados para os quadros de <b>Linhas</b> e{' '}
          <b>Colunas</b>, selecione a forma de apresentação e clique em <b>Gerar tabela</b>.
        </Alert>
        <Board<T>
          keys={dataKeyValues}
          keyMapping={keyMapping}
          handleSubmit={handleSubmit}
          handleReset={handleReset}
          sample={data[0]}
          handleAggregatorChange={handleAggregator}
          handleAggregatorKeyChange={handleAggregatorKey}
        />

        {defaultTree?.nodeValue !== undefined && (rowKeys?.length > 0 || columnKeys?.length) ? (
          <PivotTableContext.Provider value={{ total: TreeBuilder.maxLeafValue, suffix: aggregator?.suffix }}>
            <PivotTableRender
              rowKeys={rowKeys}
              columnKeys={columnKeys}
              keysMapping={keyMapping}
              defaultTree={defaultTree}
              complementaryTree={complemetaryTree}
              title={title}
              filterDataKeyValues={filterDataKeyValues}
              dateRangeFilter={dateRangeFilter}
            />
          </PivotTableContext.Provider>
        ) : null}
      </VFlow>
    )
  } else {
    return (
      <div>
        <b>Loading...</b>
      </div>
    )
  }
}
